import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Brain, 
  TrendingUp, 
  Users, 
  DollarSign,
  Target,
  Zap,
  Globe,
  ArrowRight,
  Play,
  CheckCircle,
  Star,
  Award,
  Rocket,
  Sparkles,
  BarChart3,
  MessageSquare,
  Shield,
  Clock,
  Lightbulb,
  Building,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/Button/Button';
import { cn } from '../utils/cn';

// Cycling text component for hero section
const CyclingHeroText: React.FC = () => {
  const cyclingTexts = [
    "AI-Powered Outreach",
    "Intelligent Prospecting", 
    "Revenue Acceleration",
    "Pipeline Automation",
    "Smart Lead Generation",
    "Conversion Optimization",
    "Growth Orchestration",
    "Sales Intelligence"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cyclingTexts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={currentIndex}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent"
      >
        {cyclingTexts[currentIndex]}
      </motion.span>
    </AnimatePresence>
  );
};

// Stats counter component
const StatsCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = "" 
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Intelligence",
      description: "Advanced machine learning algorithms analyze millions of data points to identify your ideal prospects with 94% accuracy.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Target,
      title: "Precision Targeting",
      description: "Find exactly who you need with natural language search. \"Find VPs of Sales at SaaS companies in California\" - it's that simple.",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: Zap,
      title: "Instant Automation",
      description: "Launch personalized campaigns in minutes, not hours. Our AI writes compelling copy that converts 3x better than traditional methods.",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: TrendingUp,
      title: "Revenue Acceleration",
      description: "Companies using Bombard see 340% more pipeline and 5x faster sales cycles. Your revenue team will thank you.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-level encryption, SOC 2 compliant, and GDPR ready. Your data is protected with military-grade security.",
      gradient: "from-indigo-500 to-blue-500"
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics",
      description: "Track every interaction, optimize every touchpoint. Our dashboard gives you insights that actually drive decisions.",
      gradient: "from-teal-500 to-cyan-500"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      title: "VP of Sales, TechFlow Solutions",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b9a83a45?w=64&h=64&crop=face",
      content: "Bombard transformed our outreach. We went from 2% to 18% response rates in 30 days. It's like having a team of AI sales experts.",
      company: "TechFlow Solutions",
      results: "+800% response rate"
    },
    {
      name: "Marcus Rodriguez",
      title: "CEO, DataVault Inc",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&crop=face",
      content: "The AI personalization is incredible. Each email feels handwritten. We closed $2.4M in new business in our first quarter.",
      company: "DataVault Inc",
      results: "$2.4M pipeline"
    },
    {
      name: "Lisa Park",
      title: "Head of Growth, CloudScale",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&crop=face",
      content: "Finding the right prospects used to take days. Now it takes minutes. Bombard's search is pure magic.",
      company: "CloudScale",
      results: "90% time saved"
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: 49,
      description: "Perfect for small teams getting started",
      features: [
        "5,000 contacts/month",
        "Basic AI personalization",
        "Email campaigns",
        "Standard support"
      ],
      highlighted: false
    },
    {
      name: "Professional",
      price: 149,
      description: "For growing sales teams",
      features: [
        "25,000 contacts/month",
        "Advanced AI insights",
        "Multi-channel campaigns",
        "Real-time analytics",
        "Priority support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: 399,
      description: "For large organizations",
      features: [
        "Unlimited contacts",
        "Custom AI models",
        "White-label options",
        "Dedicated success manager",
        "Custom integrations"
      ],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 text-white">
      {/* Navigation */}
      <nav className="relative z-50 px-6 py-4 border-b border-neutral-800/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              Bombard
            </span>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-neutral-300 hover:text-white transition-colors">Features</a>
            <a href="#testimonials" className="text-neutral-300 hover:text-white transition-colors">Testimonials</a>
            <a href="#pricing" className="text-neutral-300 hover:text-white transition-colors">Pricing</a>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/login')}
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              Login
            </Button>
            <Button 
              size="sm"
              onClick={() => navigate('/signup')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative px-6 pt-20 pb-32 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl transform scale-150"></div>
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-2xl"></div>

        <div className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300">Powered by Advanced AI • Series A Funded</span>
            </div>

            <h1 className="text-6xl md:text-8xl font-bold leading-tight">
              <div className="mb-4">The Future of</div>
              <div className="h-24 flex items-center justify-center">
                <CyclingHeroText />
              </div>
              <div className="text-neutral-300">is Here</div>
            </h1>

            <p className="text-xl md:text-2xl text-neutral-400 max-w-4xl mx-auto leading-relaxed">
              Transform your sales process with AI that understands context, personalizes at scale, 
              and drives results that seemed impossible until now.
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
              <Button 
                size="xl"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-10 py-4 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                onClick={() => navigate('/dashboard')}
              >
                <Rocket className="w-6 h-6 mr-3" />
                Get Started Free
              </Button>
              
              <Button 
                variant="outline"
                size="xl"
                className="border-2 border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 px-8 py-4 text-lg font-semibold transition-all duration-300"
                onClick={() => navigate('/onboarding')}
              >
                <Sparkles className="w-6 h-6 mr-3" />
                Smart Setup
              </Button>
              
              <motion.button
                onClick={() => setIsVideoPlaying(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-3 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors"
              >
                <div className="w-12 h-12 bg-white dark:bg-neutral-800 rounded-full flex items-center justify-center shadow-lg border border-neutral-200 dark:border-neutral-700">
                  <Play className="w-5 h-5 ml-1" />
                </div>
                <span className="font-medium">Watch Demo</span>
              </motion.button>
            </div>

            {/* Stats */}
            <div className="pt-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-blue-400">
                    <StatsCounter end={340} suffix="%" />
                  </div>
                  <div className="text-neutral-400">More Pipeline</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-purple-400">
                    <StatsCounter end={18} suffix="%" />
                  </div>
                  <div className="text-neutral-400">Response Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-cyan-400">
                    <StatsCounter end={5} suffix="x" />
                  </div>
                  <div className="text-neutral-400">Faster Sales</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-green-400">
                    <StatsCounter end={94} suffix="%" />
                  </div>
                  <div className="text-neutral-400">AI Accuracy</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-20 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                Revolutionary Features
              </span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
              Built from the ground up with cutting-edge AI to solve the biggest challenges in modern sales
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-8 bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300"
              >
                <div className={cn(
                  "w-12 h-12 bg-gradient-to-r rounded-xl flex items-center justify-center mb-6",
                  feature.gradient
                )}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-white">{feature.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="px-6 py-20 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                Loved by Leaders
              </span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
              See why top sales teams choose Bombard to accelerate their growth
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 border border-neutral-700/50 rounded-2xl"
              >
                <div className="flex items-center mb-6">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-neutral-400">{testimonial.title}</div>
                  </div>
                </div>
                <p className="text-neutral-300 mb-4 leading-relaxed">"{testimonial.content}"</p>
                <div className="text-sm text-green-400 font-semibold">
                  {testimonial.results}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                Simple Pricing
              </span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
              Start free, scale as you grow. No hidden fees, no surprises.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={cn(
                  "p-8 border rounded-2xl relative",
                  plan.highlighted 
                    ? "bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/30 scale-105" 
                    : "bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 border-neutral-700/50"
                )}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-neutral-400 mb-4">{plan.description}</p>
                  <div className="text-4xl font-bold text-white">
                    ${plan.price}
                    <span className="text-lg text-neutral-400">/month</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-neutral-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  className={cn(
                    "w-full",
                    plan.highlighted
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                      : "bg-neutral-800 hover:bg-neutral-700"
                  )}
                  onClick={() => navigate('/signup')}
                >
                  Get Started
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Ready to <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Transform</span> Your Sales?
            </h2>
            <p className="text-xl text-neutral-400 mb-8 max-w-2xl mx-auto">
              Join thousands of sales teams already using Bombard to accelerate their growth and close more deals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Button 
                size="lg"
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-lg px-8 py-4"
              >
                Start Your Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <div className="text-sm text-neutral-400">
                No credit card required • 14-day free trial
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-neutral-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                Bombard
              </span>
            </div>
            <div className="flex items-center space-x-8 text-neutral-400">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-neutral-800 text-center text-neutral-500">
            © 2025 Bombard. All rights reserved. Built with ❤️ for the future of sales.
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoPlaying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
            onClick={() => setIsVideoPlaying(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-neutral-900 rounded-2xl p-8 max-w-4xl w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="aspect-video bg-neutral-800 rounded-xl flex items-center justify-center">
                <div className="text-center text-neutral-400">
                  <Play className="w-16 h-16 mx-auto mb-4" />
                  <p>Demo video coming soon</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage; 